import { useMemo, useEffect, useRef, useState } from "react";
import { NOTIFICATION_STATUSES } from "../notifications.consts";
import { SECTION_TYPE, IN_PROGRESS_TITLE, COMPLETED_TITLE, HEADER_HEIGHT, ITEM_HEIGHT, LOADING_HEIGHT, ITEM_HEIGHT_WITH_PUBLISH_DESCRIPTION } from "./NotificationsPanel.consts";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getNotificationStatus } from "../notifications.utils";
import useAppStore from "src/stores/appStore";

const useNotificationsPanel = (parentRef, open, onClose) => {
  const [selectedNotification, setSelectedNotification] = useState(null);

  const {
    notifications,
    notificationsCount,
    isInitialNotificationsLoading: isLoading,
    isFetchingNextNotificationsPage: isFetchingNextPage,
    hasMoreNotifications: hasNextPage,
    fetchNextNotificationsPage: fetchNextPage,
    markNotificationsAsReadViaSocket
  } = useAppStore();

  const [inProgress, completed] = useMemo(() => {
    const inProgressItems = [];
    const completedItems = [];

    notifications?.forEach((notification) => {
      const normalizedNotification = {
        ...notification,
        status: getNotificationStatus(notification?.steps)
      };
      if (normalizedNotification?.status === NOTIFICATION_STATUSES.IN_PROGRESS) {
        inProgressItems.push(normalizedNotification);
      } else {
        completedItems.push(normalizedNotification);
      }
    });

    return [inProgressItems, completedItems];
  }, [notifications]);

  const virtualRows = useMemo(() => {
    const rows = [];

    if (inProgress.length > 0) {
      rows.push({ key: "in-progress-title", type: SECTION_TYPE.HEADER, title: IN_PROGRESS_TITLE });
      inProgress.forEach((notification) => rows.push({ key: notification.id, type: SECTION_TYPE.ITEM, notification }));
    }

    if (completed.length > 0) {
      rows.push({ key: "completed-title", type: SECTION_TYPE.HEADER, title: COMPLETED_TITLE });
      completed.forEach((notification) => rows.push({ key: notification.id, type: SECTION_TYPE.ITEM, notification }));
    }

    if (isFetchingNextPage) {
      rows.push({ key: "loading", type: SECTION_TYPE.LOADING });
    }

    return rows;
  }, [inProgress, completed, isFetchingNextPage]);

  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => parentRef?.current,
    estimateSize: (index) => {
      const row = virtualRows[index];
      if (!row) return ITEM_HEIGHT;
      if (row.type === SECTION_TYPE.HEADER) return HEADER_HEIGHT;
      if (row.type === SECTION_TYPE.LOADING) return LOADING_HEIGHT;
      return row.notification?.publishDescription ? ITEM_HEIGHT_WITH_PUBLISH_DESCRIPTION : ITEM_HEIGHT;
    },
    overscan: 4
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current && notificationsCount > 0) {
      markNotificationsAsReadViaSocket();
    }
    prevOpenRef.current = open;
  }, [open, notificationsCount, markNotificationsAsReadViaSocket]);

  useEffect(() => {
    if (open) {
      // We need to set a timeout to measure the height of the rows after the drawer is opened
      setTimeout(() => rowVirtualizer.measure());
    }
  }, [open, rowVirtualizer]);

  // Fetch next page when the last notification item is visible in the viewport
  useEffect(() => {
    const lastItem = virtualItems.at(-1);

    if (!lastItem) return;

    if (
      lastItem.index >= notifications.length - 1 &&
            hasNextPage &&
            !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, notifications.length, isFetchingNextPage, virtualItems
  ]);

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
  };

  const handleClose = () => {
    setSelectedNotification(null);
    onClose();
  };

  const handleBack = () => {
    setSelectedNotification(null);
  };

  return {
    virtualRows,
    rowVirtualizer,
    notifications: virtualItems,
    hasNotifications: notifications.length > 0,
    isFetchingNextPage,
    isLoading,
    selectedNotification,
    handleNotificationClick,
    handleClose,
    handleBack
  };
};

export default useNotificationsPanel;
